import { type TopicMessage, useWebSocketTopic } from '@src/shared/websocket';
import { useEffect, useRef } from 'react';

const REFRESH_DEBOUNCE_MS = 100;

/**
 * Subscribe to an invalidation topic and refresh a query on every message.
 *
 * The counterpart to a *snapshot* topic like `users.presence`, whose messages
 * carry the new state and are applied in place. An invalidation topic only
 * says "something changed", so the client has to go and ask -- which means
 * one event fans out into a refetch per connected browser. That cost is
 * accepted here because task and alert events are comparatively rare; do not
 * reach for this shape on a topic whose event rate scales with the audience.
 *
 * `onRefresh` fires on connect, after every message, and when the tab becomes
 * visible again -- the last of these even if the socket never dropped, since
 * a backgrounded tab can miss things the socket alone cannot detect.
 */
export default function useTopicRefresh(
  topic: string,
  onMessage: (message: TopicMessage) => void,
  onRefresh: () => void,
) {
  const callbacks = useRef({ onMessage, onRefresh });
  callbacks.current = { onMessage, onRefresh };

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleRefresh = useRef(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      callbacks.current.onRefresh();
    }, REFRESH_DEBOUNCE_MS);
  });

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') scheduleRefresh.current();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);

  useWebSocketTopic(
    topic,
    (message) => {
      callbacks.current.onMessage(message);
      scheduleRefresh.current();
    },
    { onReconnect: () => scheduleRefresh.current() },
  );
}
